import { useEffect, useMemo, useRef, useState } from 'react';
import { zebraPrintService } from '#client/services/zebraPrintService';

export function useZebraPrinterSelection({ enabled, locations, defaultLocationGid }) {
    const [selectedLocationGid, setSelectedLocationGid] = useState('');
    const [selectedPrinterDeviceId, setSelectedPrinterDeviceId] = useState('');
    const previousDefaultRef = useRef(defaultLocationGid);

    const selectedLocation = useMemo(() => {
        if (!selectedLocationGid) return null;
        return (locations || []).find((l) => l.locationGid === selectedLocationGid) || null;
    }, [locations, selectedLocationGid]);

    const availablePrinters = useMemo(() => {
        return selectedLocation?.labelPrinters ?? [];
    }, [selectedLocation]);

    // When defaultLocationGid changes, switch to that location (supports both locationId and locationGid)
    useEffect(() => {
        if (!enabled) return;
        if (!Array.isArray(locations) || locations.length === 0) return;

        const previousDefault = previousDefaultRef.current;
        if (previousDefault === defaultLocationGid) return;

        previousDefaultRef.current = defaultLocationGid;
        if (!defaultLocationGid) return;

        // Find location by matching either locationGid or id (locationId)
        const matchedLocation = locations.find((loc) => loc.locationGid === defaultLocationGid || String(loc.id) === String(defaultLocationGid));

        if (matchedLocation && matchedLocation.locationGid !== selectedLocationGid) {
            setSelectedLocationGid(matchedLocation.locationGid);
            setSelectedPrinterDeviceId(''); // Reset printer when location changes
        }
    }, [defaultLocationGid, enabled, locations, selectedLocationGid]);

    // Default the selected location (prefer defaultLocationGid if provided, otherwise saved selection)
    useEffect(() => {
        if (!enabled) return;
        if (!Array.isArray(locations) || locations.length === 0) return;
        if (selectedLocationGid) return;

        const saved = zebraPrintService.getSelectedZebraPrinter();

        // If defaultLocationGid is provided, prioritize it
        if (defaultLocationGid) {
            const matchedLocation = locations.find((loc) => loc.locationGid === defaultLocationGid || String(loc.id) === String(defaultLocationGid));
            if (matchedLocation) {
                // Only use saved location if it matches the defaultLocationGid
                if (saved?.locationGid === matchedLocation.locationGid) {
                    setSelectedLocationGid(saved.locationGid);
                } else {
                    setSelectedLocationGid(matchedLocation.locationGid);
                }
                return;
            }
        }

        // No defaultLocationGid provided, fall back to saved selection
        if (saved?.locationGid && locations.some((l) => l.locationGid === saved.locationGid)) {
            setSelectedLocationGid(saved.locationGid);
        }
    }, [defaultLocationGid, enabled, locations, selectedLocationGid]);

    // Default the selected printer from saved selection (once location is selected)
    useEffect(() => {
        if (!enabled) return;
        if (!selectedLocationGid) return;
        if (selectedPrinterDeviceId) return;

        const saved = zebraPrintService.getSelectedZebraPrinter();
        if (!saved?.deviceId || saved.locationGid !== selectedLocationGid) return;

        const stillValid = availablePrinters.some((p) => p.deviceId === saved.deviceId);
        if (stillValid) setSelectedPrinterDeviceId(saved.deviceId);
    }, [availablePrinters, enabled, selectedLocationGid, selectedPrinterDeviceId]);

    // Keep printer selection valid when location changes
    useEffect(() => {
        if (!enabled) return;

        if (!selectedLocationGid) {
            setSelectedPrinterDeviceId('');
            return;
        }

        if (!selectedPrinterDeviceId) return;
        const stillValid = availablePrinters.some((p) => p.deviceId === selectedPrinterDeviceId);
        if (!stillValid) setSelectedPrinterDeviceId('');
    }, [availablePrinters, enabled, selectedLocationGid, selectedPrinterDeviceId]);

    // Persist selection whenever both location and printer are selected (and valid)
    useEffect(() => {
        if (!enabled) return;
        if (!selectedLocationGid || !selectedPrinterDeviceId) return;

        const stillValid = availablePrinters.some((p) => p.deviceId === selectedPrinterDeviceId);
        if (!stillValid) return;

        zebraPrintService.setSelectedZebraPrinter(selectedLocationGid, selectedPrinterDeviceId);
    }, [availablePrinters, enabled, selectedLocationGid, selectedPrinterDeviceId]);

    const locationOptions = useMemo(() => {
        const options = [{ label: 'Select a location...', value: '', disabled: true }];
        (locations || []).forEach((loc) => {
            options.push({ label: loc.name || loc.locationGid, value: loc.locationGid });
        });
        return options;
    }, [locations]);

    const printerOptions = useMemo(() => {
        const options = [{ label: selectedLocationGid ? 'Select a printer...' : 'Select a location first', value: '', disabled: true }];
        (availablePrinters || []).forEach((p) => {
            options.push({ label: p.deviceId, value: p.deviceId });
        });
        return options;
    }, [availablePrinters, selectedLocationGid]);

    const printerHelpText = useMemo(() => {
        return selectedLocationGid && availablePrinters.length === 0 ? 'No printers configured for this location. Ask an admin to add a printer in Settings → Label Printers.' : undefined;
    }, [availablePrinters.length, selectedLocationGid]);

    const getSelectionError = useMemo(() => {
        return () => {
            if (!selectedLocationGid) return 'Please select a location';
            if (!selectedPrinterDeviceId) return 'Please select a printer';
            return null;
        };
    }, [selectedLocationGid, selectedPrinterDeviceId]);

    const resetSelection = useMemo(() => {
        return () => {
            setSelectedLocationGid('');
            setSelectedPrinterDeviceId('');
        };
    }, []);

    return {
        selectedLocationGid,
        setSelectedLocationGid,
        selectedPrinterDeviceId,
        setSelectedPrinterDeviceId,
        selectedLocation,
        availablePrinters,
        locationOptions,
        printerOptions,
        printerHelpText,
        getSelectionError,
        resetSelection,
    };
}
